import { useState } from 'react';
import { formatRial } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { Button, Card, Kpi, Textarea, useToast } from '../ui';

/**
 * مغایرت‌گیری بانکی — paste a bank statement (each line «تاریخ، مبلغ، شرح»; amount +واریز / −برداشت) and
 * match it against the Bank ledger. Shows matched count, what's unmatched on each side, and the gap.
 * Read-only (accounting.read).
 */

interface BookLine {
  ref: string;
  date: string;
  amount: number;
  memo: string;
}
interface StmtLine {
  date: string;
  amount: number;
  desc: string;
}
interface Recon {
  matched: unknown[];
  unmatchedBook: BookLine[];
  unmatchedStatement: StmtLine[];
  bookTotal: number;
  statementTotal: number;
  difference: number;
}

export function BankReconPanel({ onDraft }: { onDraft?: (s: StmtLine) => void } = {}) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState<Recon | null>(null);

  function parse(): StmtLine[] {
    return text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [date, amount, ...rest] = l.split(/[,\t]/);
        return {
          date: (date ?? '').trim(),
          amount: Math.round(Number((amount ?? '0').replace(/[^0-9.-]/g, '')) || 0),
          desc: rest.join(' ').trim(),
        };
      })
      .filter((s) => s.date || s.amount);
  }

  async function run() {
    const statement = parse();
    if (statement.length === 0) {
      toast('صورت‌حساب بانکی را وارد کنید', 'error');
      return;
    }
    setBusy(true);
    const r = await api<Recon>('/api/accounting/bank-reconcile', {
      method: 'POST',
      body: JSON.stringify({ statement }),
    });
    setBusy(false);
    if (!r.ok) {
      toast(errMsg(r, 'خطا در مغایرت‌گیری'), 'error');
      return;
    }
    setRec(r.body as Recon);
  }

  return (
    <Card title="مغایرت‌گیری بانکی">
      <p style={{ color: 'var(--ink-3)', marginTop: 0 }}>
        صورت‌حساب بانک را هر خط «تاریخ، مبلغ، شرح» بچسبانید (مبلغ مثبت = واریز، منفی = برداشت). با
        اسناد حساب «بانک» تطبیق داده می‌شود.
      </p>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder={
          '2026-07-01,5000000,واریز چک\n2026-07-03,-2000000,برداشت\n2026-07-04,-50000,کارمزد'
        }
        style={{ direction: 'ltr', textAlign: 'left', fontVariantNumeric: 'tabular-nums' }}
      />
      <div style={{ marginTop: 10 }}>
        <Button onClick={() => void run()} disabled={busy}>
          {busy ? 'در حال تطبیق…' : 'مغایرت‌گیری'}
        </Button>
      </div>

      {rec ? (
        <div style={{ marginTop: 12 }}>
          <div className="kpi-grid">
            <Kpi
              label="تطبیق‌شده"
              value={rec.matched.length}
              tone={
                rec.unmatchedBook.length === 0 && rec.unmatchedStatement.length === 0
                  ? 'good'
                  : undefined
              }
            />
            <Kpi label="مانده در دفتر (بدون بانک)" value={rec.unmatchedBook.length} />
            <Kpi label="در بانک (بدون دفتر)" value={rec.unmatchedStatement.length} />
            <Kpi
              label="اختلاف"
              value={formatRial(rec.difference)}
              tone={rec.difference === 0 ? 'good' : 'bad'}
            />
          </div>

          {rec.unmatchedBook.length > 0 ? (
            <div style={{ marginTop: 10 }}>
              <b>در دفتر ثبت شده، در صورت‌حساب بانک نیست (چک وصول‌نشده و…):</b>
              <table className="table" style={{ marginTop: 6 }}>
                <thead>
                  <tr>
                    <th>سند</th>
                    <th>تاریخ</th>
                    <th>مبلغ</th>
                    <th>شرح</th>
                  </tr>
                </thead>
                <tbody>
                  {rec.unmatchedBook.map((b, i) => (
                    <tr key={i}>
                      <td>{b.ref}</td>
                      <td>{b.date}</td>
                      <td>{formatRial(b.amount)}</td>
                      <td>{b.memo || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {rec.unmatchedStatement.length > 0 ? (
            <div style={{ marginTop: 10 }}>
              <b>در صورت‌حساب بانک هست، در دفتر ثبت نشده (کارمزد، برداشت مستقیم و…):</b>
              <table className="table" style={{ marginTop: 6 }}>
                <thead>
                  <tr>
                    <th>تاریخ</th>
                    <th>مبلغ</th>
                    <th>شرح</th>
                    {onDraft ? <th></th> : null}
                  </tr>
                </thead>
                <tbody>
                  {rec.unmatchedStatement.map((s, i) => (
                    <tr key={i}>
                      <td>{s.date}</td>
                      <td>{formatRial(s.amount)}</td>
                      <td>{s.desc || '—'}</td>
                      {onDraft ? (
                        <td>
                          <Button variant="secondary" onClick={() => onDraft(s)}>
                            ثبت سند
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
